/**
 * MasterAdminDashboardPage
 *
 * Network-level overview for the MASTER_ADMIN role. Replaces the vehicle-level
 * Vehicle Demand panels (which are dealer-facing data) with KPIs that are
 * meaningful at the platform level: total dealers, resellers, dealer groups,
 * and the active subset of dealers.
 *
 * Resellers (SUB_MASTER) and Dealers continue to see VehicleDemandPage and
 * the dealer dashboard respectively — only this page replaces the master
 * admin's "Dashboard" nav target.
 */
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Building2, Handshake, Briefcase, ClipboardList, ArrowRight } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, hint, accent = "purple", testid }) => {
  const accents = {
    purple: { bg: "bg-purple-50", icon: "text-purple-600" },
    blue: { bg: "bg-blue-50", icon: "text-blue-600" },
    emerald: { bg: "bg-emerald-50", icon: "text-emerald-600" },
    amber: { bg: "bg-amber-50", icon: "text-amber-600" },
  };
  const a = accents[accent] || accents.purple;
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg ${a.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${a.icon}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5" data-testid={`${testid}-value`}>
              {value}
            </p>
            {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickLinkCard = ({ icon: Icon, title, description, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left bg-white border border-gray-200 rounded-lg p-5 hover:border-purple-300 hover:shadow-sm transition-all group"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-1.5">
      <Icon className="w-4 h-4 text-purple-600" />
      <span className="font-semibold text-gray-900">{title}</span>
      <ArrowRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-purple-600 group-hover:translate-x-0.5 transition-all" />
    </div>
    <p className="text-sm text-gray-500">{description}</p>
  </button>
);

const MasterAdminDashboardPage = ({ onNavigate } = {}) => {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDealers: 0,
    activeDealers: 0,
    totalResellers: 0,
    totalGroups: 0,
  });

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [dealersRes, activeRes, resellersRes, groupsRes] = await Promise.all([
        apiFetch("/admin/dealers?limit=1&include_archived=true", { method: "GET" }),
        apiFetch("/admin/dealers?limit=1&include_archived=false", { method: "GET" }),
        apiFetch("/admin/resellers", { method: "GET" }),
        apiFetch("/admin/dealer-groups", { method: "GET" }),
      ]);
      setStats({
        totalDealers: dealersRes.data?.total || 0,
        activeDealers: activeRes.data?.total || 0,
        totalResellers: resellersRes.data?.total || 0,
        totalGroups: groupsRes.data?.total || 0,
      });
    } catch {
      // Non-fatal — leave zeros visible
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const go = (page) => {
    if (typeof onNavigate === "function") onNavigate(page);
    else window.location.assign(`/?page=${page}`);
  };

  return (
    <div className="p-6 w-full" data-testid="master-admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Network Overview</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Platform-level metrics across all dealers, resellers, and groups.
        </p>
      </div>

      {/* KPI grid */}
      <div
        className="gap-4 mb-8"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        }}
      >
        {loading ? (
          [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)
        ) : (
          <>
            <KpiCard
              icon={Building2}
              label="Total Dealers"
              value={stats.totalDealers}
              hint={`${stats.activeDealers} active · ${Math.max(stats.totalDealers - stats.activeDealers, 0)} archived`}
              accent="purple"
              testid="kpi-total-dealers"
            />
            <KpiCard
              icon={Building2}
              label="Active Dealers"
              value={stats.activeDealers}
              hint="Excluding archived"
              accent="emerald"
              testid="kpi-active-dealers"
            />
            <KpiCard
              icon={Handshake}
              label="Resellers"
              value={stats.totalResellers}
              hint="Sub-master accounts"
              accent="blue"
              testid="kpi-total-resellers"
            />
            <KpiCard
              icon={Briefcase}
              label="Dealer Groups"
              value={stats.totalGroups}
              hint="Multi-rooftop auto groups"
              accent="amber"
              testid="kpi-total-groups"
            />
          </>
        )}
      </div>

      {/* Quick links */}
      <div className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Manage</h2>
        <div
          className="gap-3"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          }}
        >
          <QuickLinkCard
            icon={Building2}
            title="Dealer Management"
            description="Onboard, configure, and audit individual dealer rooftops."
            onClick={() => go("admin-dealers")}
            testid="quicklink-dealers"
          />
          <QuickLinkCard
            icon={Handshake}
            title="Reseller Management"
            description="Create resellers and assign dealers to their portfolios."
            onClick={() => go("admin-resellers")}
            testid="quicklink-resellers"
          />
          <QuickLinkCard
            icon={Briefcase}
            title="Dealer Groups"
            description="Group multiple rooftops under a single auto group."
            onClick={() => go("admin-dealer-groups")}
            testid="quicklink-groups"
          />
          <QuickLinkCard
            icon={ClipboardList}
            title="Audit Log"
            description="Trace every admin action across the network."
            onClick={() => go("audit-log")}
            testid="quicklink-audit"
          />
        </div>
      </div>

      {/* Why no vehicle data here */}
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-700">
            Looking for vehicle-level analytics?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Per-vehicle views, unlocks, and lead activity are dealer-facing data and live on each
            dealer&apos;s detail page. Open{" "}
            <button
              type="button"
              onClick={() => go("admin-dealers")}
              className="text-purple-700 font-medium hover:underline"
              data-testid="goto-dealer-management"
            >
              Dealer Management
            </button>
            {" "}and select a dealer to drill into their inventory analytics, leads, and pricing rules.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MasterAdminDashboardPage;
